import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import menuItems from './menuConfig';
import './HeaderStyles.css';
import Logo from '../../assets/new_logo.svg';
import useHeaderVisibility from '../../hooks/useHeaderVisibility';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { generateClient } from 'aws-amplify/api';
import { listUserProfiles } from '../../graphql/queries';

const Header = () => {
  const isVisible = useHeaderVisibility();
  const { user, signOut } = useAuthenticator();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userFirstName, setUserFirstName] = useState('');
  const menuRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // ✅ Patch: memoise the client so it doesn't cause useEffect to rerun
  const client = useRef(generateClient()).current;

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) {
        setUserFirstName('');
        return;
      }

      try {
        const { data } = await client.graphql({
          query: listUserProfiles,
          variables: {
            filter: { owner: { eq: user.username } }
          },
          authMode: 'userPool'
        });

        if (data.listUserProfiles.items.length > 0) {
          const profile = data.listUserProfiles.items[0];
          const firstName = profile.name?.trim()?.split(' ')?.[0] || 'User';
          setUserFirstName(firstName);
        } else {
          setUserFirstName('User');
        }
      } catch (error) {
        console.error('Error fetching user profile for header:', error);
        setUserFirstName('User');
      }
    };

    fetchUserProfile();
  }, [user]); // ✅ Only depend on user, not client

  // Close mobile menu if screen size changes to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toggleButtonRef.current && toggleButtonRef.current.contains(event.target)) {
        return;
      }

      if (menuRef.current && !menuRef.current.contains(event.target) && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header className={`header ${isVisible ? 'visible' : 'hidden'}`}>
        <NavLink to="/" end className="header-logo" onClick={() => setIsMobileMenuOpen(false)}>
          <img src={Logo} alt="MedMap Logo" className="logo-image" />
        </NavLink>

        {/* Mobile-only centered Login button */}
        {!user && (
          <NavLink
            to="/login"
            end
            className="mobile-center-login"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Login
          </NavLink>
        )}

        {/* Mobile-only centered Home button */}
        {user && (
          <NavLink
            to="/"
            end
            className="mobile-center-home"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </NavLink>
        )}

        <button
          ref={toggleButtonRef}
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          <div className="hamburger">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>

        <nav ref={menuRef} className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {/* Mobile top actions: show when signed in */}
          {user && (
            <div className="mobile-menu-top-actions">
              <div className="mobile-header-buttons">
                <NavLink
                  to="/user"
                  className="quiz-button"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profile
                </NavLink>
                <button
                  className="signout-button"
                  onClick={() => { setIsMobileMenuOpen(false); signOut(); }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          )}

          {menuItems.map((item) => (
            <div key={item.title} className="menu-item">
              <NavLink
                to={item.link}
                end
                className={({ isActive }) => (isActive ? 'active' : undefined)}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.title}
              </NavLink>
              {item.submenu && (
                <div className="submenu">
                  {item.submenu.map((sub) => (
                    <NavLink
                      key={sub.title}
                      to={sub.link}
                      end
                      className={({ isActive }) => (isActive ? 'active' : undefined)}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {sub.title}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="header-buttons">
          <NavLink to="/quiz-choice" end className="quiz-button">
            Take the Quiz
          </NavLink>
          {user && (
            <NavLink to="/pathfinder" end className="quiz-button">
              Pathfinder
            </NavLink>
          )}
          {user ? (
            <div className="user-info">
              <NavLink to="/user" className="user-link">
                <span className="welcome-message">
                  Welcome, {userFirstName || 'User'}!
                </span>
              </NavLink>
              <button onClick={signOut} className="signout-button">
                Sign Out
              </button>
            </div>
          ) : (
            <NavLink to="/login" end className="login-button">
              Login
            </NavLink>
          )}
        </div>
      </header>

      <div
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      ></div>
    </>
  );
};

export default Header;